import resolveUpscCmsAssetUrl from './upscCmsAssetUrl';

type MarkdownBlock =
  | { type: 'paragraph'; lines: string[] }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'image'; alt: string; src: string };

const LINE_BREAK = '\u0000';

const buildImgTag = (src: string, alt: string) =>
  `<img src="${escapeHtml(resolveUpscCmsAssetUrl(src))}" alt="${escapeHtml(
    alt
  )}" loading="lazy" />`;

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const HTML_TAG_PATTERN = /(<[^>]+>)/g;

const hasHtmlTags = (content: string) => /<[a-z][^>]*>/i.test(content);

const formatInlineMarkdownInHtml = (text: string) =>
  text
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, src) =>
      buildImgTag(src, alt)
    )
    .replace(
      /\*\*([^*]+)\*\*/g,
      (_, content) => `<strong>${escapeHtml(content)}</strong>`
    )
    .replace(/\*([^*]+)\*/g, (_, content) => `<em>${escapeHtml(content)}</em>`);

const htmlWithInlineMarkdown = (html: string) =>
  html
    .split(HTML_TAG_PATTERN)
    .map((part) =>
      part.startsWith('<') ? part : formatInlineMarkdownInHtml(part)
    )
    .join('');

const formatInlineMarkdown = (text: string) => {
  const htmlSnippets: string[] = [];

  const stash = (html: string) => {
    const token = `@@HTML_${htmlSnippets.length}@@`;
    htmlSnippets.push(html);
    return token;
  };

  let formatted = text
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, src) =>
      stash(buildImgTag(src, alt))
    )
    .replace(/\*\*([^*]+)\*\*/g, (_, content) =>
      stash(`<strong>${escapeHtml(content)}</strong>`)
    )
    .replace(/\*([^*]+)\*/g, (_, content) =>
      stash(`<em>${escapeHtml(content)}</em>`)
    );

  formatted = escapeHtml(formatted);

  htmlSnippets.forEach((html, index) => {
    formatted = formatted.replace(`@@HTML_${index}@@`, html);
  });

  return formatted;
};

const parseMarkdownBlocks = (markdown: string): MarkdownBlock[] => {
  const blocks: MarkdownBlock[] = [];
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');

  let paragraphLines: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let listItems: string[] = [];

  const flushParagraph = () => {
    while (
      paragraphLines.length &&
      paragraphLines[paragraphLines.length - 1] === LINE_BREAK
    ) {
      paragraphLines.pop();
    }

    if (!paragraphLines.length) {
      return;
    }

    blocks.push({ type: 'paragraph', lines: [...paragraphLines] });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listType || !listItems.length) {
      listType = null;
      listItems = [];
      return;
    }

    blocks.push({ type: listType, items: [...listItems] });
    listType = null;
    listItems = [];
  };

  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      if (listType) {
        flushList();
      } else if (paragraphLines.length) {
        const lastLine = paragraphLines[paragraphLines.length - 1];
        if (lastLine !== LINE_BREAK) {
          paragraphLines.push(LINE_BREAK);
        }
      }
      return;
    }

    const imageMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imageMatch) {
      flushAll();
      blocks.push({
        type: 'image',
        alt: imageMatch[1],
        src: imageMatch[2]
      });
      return;
    }

    const unorderedMatch = trimmed.match(/^-\s+(.+)$/);
    if (unorderedMatch) {
      flushParagraph();
      if (listType === 'ol') {
        flushList();
      }
      listType = 'ul';
      listItems.push(unorderedMatch[1]);
      return;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph();
      if (listType === 'ul') {
        flushList();
      }
      listType = 'ol';
      listItems.push(orderedMatch[1]);
      return;
    }

    flushList();
    paragraphLines.push(trimmed);
  });

  flushAll();
  return blocks;
};

const paragraphToHtml = (lines: string[]) => {
  const parts: string[] = [];

  lines.forEach((line, index) => {
    if (line === LINE_BREAK) {
      parts.push('<br />');
      return;
    }

    const prevLine = index > 0 ? lines[index - 1] : null;
    if (index > 0 && prevLine !== LINE_BREAK) {
      parts.push('<br />');
    }

    parts.push(formatInlineMarkdown(line));
  });

  return `<p>${parts.join('')}</p>`;
};

const blockToHtml = (block: MarkdownBlock) => {
  switch (block.type) {
    case 'paragraph':
      return paragraphToHtml(block.lines);
    case 'ul':
      return `<ul>${block.items
        .map((item) => `<li>${formatInlineMarkdown(item)}</li>`)
        .join('')}</ul>`;
    case 'ol':
      return `<ol>${block.items
        .map((item) => `<li>${formatInlineMarkdown(item)}</li>`)
        .join('')}</ol>`;
    case 'image':
      return buildImgTag(block.src, block.alt);
    default:
      return '';
  }
};

const truncateByMaxLines = (content: string, maxLines?: number) => {
  if (!maxLines) return content;

  const lines = content.replace(/\r\n/g, '\n').split('\n');
  if (lines.length <= maxLines) {
    return content;
  }

  return lines.slice(0, maxLines).join('\n');
};

const upscCmsMarkdownToHtml = (markdown: string, maxLines?: number) => {
  if (!markdown?.trim()) {
    return '';
  }

  const content = truncateByMaxLines(markdown, maxLines);
  return parseMarkdownBlocks(content).map(blockToHtml).join('');
};

export const upscCmsContentToHtml = (content: string, maxLines?: number) => {
  if (!content?.trim()) {
    return '';
  }

  const truncated = truncateByMaxLines(content, maxLines);

  if (!hasHtmlTags(truncated)) {
    return upscCmsMarkdownToHtml(truncated);
  }

  return htmlWithInlineMarkdown(truncated);
};

export default upscCmsMarkdownToHtml;
